import {
  BarChartOutlined,
  CarOutlined,
  CopyOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  TwitterOutlined,
  UserOutlined,
  MoneyCollectOutlined,
  RedEnvelopeOutlined,
  UserAddOutlined,
  BlockOutlined,
  PaperClipOutlined,
  FileExclamationOutlined,
  HeartOutlined,
  AccountBookOutlined,
  HomeOutlined,
  CompressOutlined,
  MergeCellsOutlined,
  FormOutlined,
  ClockCircleOutlined,
  IssuesCloseOutlined,
  ControlOutlined,
  WarningOutlined,
  ApiOutlined,
} from "@ant-design/icons";
import { Layout, Menu } from "antd";
import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AgentTabs,
  AuditTabs,
  BusTabs,
  DatabaseTabs,
  ERCATabs,
  ReportTabs,
  TicketTabs,
  TripTabs,
  UserTabs,
  AccessControlTabs,
} from "../../../constants/constants";
import { ROUTES } from "../../../constants/routes";
import { generateRouteString } from "../../../utils/utility";
import Logo from "/src/assets/images/logo.jpg";
import { useAppDispatch, useTypedSelector } from "../../../redux/redux-hooks";
import { AppDispatch, BusAdminActions, RootState } from "../../../redux/Store";
import { useAuth } from "../../../context/auth-context";
import { ItemType, MenuItemType } from "antd/es/menu/interface";
import { AccessControlActions } from "../../../redux/Slices/Access-control/Access-control.slice";

const { Sider } = Layout;

const SideNavComponent: FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch: AppDispatch = useAppDispatch();
  const busAdminState = useTypedSelector(
    (state: RootState) => state.bus_admin.busAdmin
  );

  const [tab, setTab] = useState<string>(ROUTES.ROOT);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const loginState = useTypedSelector((state) => state.bus_admin.login);
  const { roles } = useTypedSelector((state) => state.access_control);

  const { hasFeature, hasPermission, isAuthenticated } = useAuth();

  useEffect(() => {
    onLocationChange(location);
  }, [location]);

  useEffect(() => {
    // Fetch profile if user is authenticated and profile is not loaded
    if (isAuthenticated && !busAdminState.payload && !busAdminState.loading) {
      dispatch(BusAdminActions.getBusAdmin(""));
    }
  }, [isAuthenticated, busAdminState.payload, busAdminState.loading, dispatch]);

  // Fetch roles for the User tab in sidenav
  useEffect(() => {
    if (
      isAuthenticated &&
      hasFeature("bus-admin") &&
      !roles.payload?.length &&
      !roles.loading &&
      !roles.error // Don't fetch if there's an error (e.g., 403 Forbidden)
    ) {
      dispatch(AccessControlActions.fetchAllRoles());
    }
  }, [isAuthenticated, hasFeature, roles.payload, roles.loading, roles.error, dispatch]);

  const onLocationChange = (route: any) => {
    const fullPath = route?.pathname;

    switch (route?.pathname.split("/")[1]) {
      case ROUTES.AGENT.split("/")[1]:
        if (fullPath === ROUTES.AGENT) setTab(ROUTES.AGENT);
        break;
      case ROUTES.BUS.split("/")[1]:
        if (fullPath === ROUTES.BUS) setTab(ROUTES.BUS);
        break;
      case ROUTES.DATABASE.split("/")[1]:
        if (fullPath === ROUTES.DATABASE) setTab(ROUTES.DATABASE);
        break;
      case ROUTES.TRIP.split("/")[1]:
        if (fullPath === ROUTES.TRIP) setTab(ROUTES.TRIP);
        break;
      case ROUTES.USER.split("/")[1]:
        if (fullPath === ROUTES.USER) setTab(ROUTES.USER);
        break;
      case ROUTES.TICKET.split("/")[1]:
        if (fullPath === ROUTES.TICKET) setTab(ROUTES.TICKET);
        break;
      case ROUTES.AUDIT.split("/")[1]:
        if (fullPath === ROUTES.AUDIT) setTab(ROUTES.AUDIT);
        break;
      case ROUTES.REPORT.split("/")[1]:
        if (fullPath === ROUTES.REPORT) setTab(ROUTES.REPORT);
        break;
      case ROUTES.ERCA.split("/")[1]:
        if (fullPath === ROUTES.ERCA) setTab(ROUTES.ERCA);
        break;
      case ROUTES.ERP_INTEGRATION.split("/")[1]:
        if (fullPath === ROUTES.ERP_INTEGRATION) setTab(ROUTES.ERP_INTEGRATION);
        break;
      case ROUTES.MOLT_INTEGRATION.split("/")[1]:
        if (fullPath === ROUTES.MOLT_INTEGRATION)
          setTab(ROUTES.MOLT_INTEGRATION);
        break;
      default:
        setTab(route?.pathname);
        break;
    }
  };

  const onTabSelect = (e: any) => {
    const path = e.key;
    if (path) {
      setTab(path);
      navigate(path);

      // Scroll to top of the page when navigating
      window.scrollTo({ top: 0, behavior: "smooth" });

      const parentKey = e.keyPath?.[1];
      if (parentKey) {
        setOpenKeys([parentKey]);
      } else {
        // If no parent (top-level item like Dashboard), close all menus
        setOpenKeys([]);
      }
    }
  };

  const onOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  const getMenuItems = (): any[] => {
    const item: any[] = [];

    item.push({
      key: ROUTES.ROOT,
      icon: <BarChartOutlined />,
      label: "Dashboard",
    });

    if (hasFeature("access-control")) {
      const accessControlChildren: ItemType<MenuItemType>[] = [];

      accessControlChildren.push({
        key: `${ROUTES.ACCESS_CONTROL}/${generateRouteString(
          AccessControlTabs.ROLE
        )}`,
        icon: <UserOutlined />,
        label: AccessControlTabs.ROLE,
      });

      accessControlChildren.push({
        key: `${ROUTES.ACCESS_CONTROL}/${generateRouteString(
          AccessControlTabs.ROLE_PERMISSION
        )}`,
        icon: <WarningOutlined />,
        label: AccessControlTabs.ROLE_PERMISSION,
      });

      item.push({
        key: ROUTES.ACCESS_CONTROL,
        icon: <ControlOutlined />,
        label: "Access Control",
        children: accessControlChildren,
      });
    }

    if (hasFeature("agent")) {
      const agentChildren: any[] = [];

      if (hasPermission("agent", "agent:view-paged")) {
        agentChildren.push({
          key: `${ROUTES.AGENT}/${generateRouteString(AgentTabs.REGISTRY)}`,
          label: "Registry",
          icon: <UserOutlined />,
        });
      }

      if (
        hasPermission("agent-topup-request", "agent-topup-request:view-paged")
      ) {
        agentChildren.push({
          key: `${ROUTES.AGENT}/${generateRouteString(
            AgentTabs.TOP_UP_REQUEST
          )}`,
          label: "Top Up Request",
          icon: <MoneyCollectOutlined />,
        });
      }

      if (agentChildren.length > 0) {
        item.push({
          key: ROUTES.AGENT,
          icon: <UserOutlined />,
          label: "Agent",
          children: agentChildren,
        });
      }
    }

    if (hasFeature(["conductor", "driver", "bus", "bus-structure"])) {
      const busChildren: any[] = [];

      if (hasPermission("bus", "bus:view-paged")) {
        busChildren.push({
          label: BusTabs.BUS,
          key: `${ROUTES.BUS}/${generateRouteString(BusTabs.BUS)}`,
          icon: <UserAddOutlined />,
        });
      }

      if (hasPermission("driver", "driver:view-paged")) {
        busChildren.push({
          label: BusTabs.DRIVER,
          key: `${ROUTES.BUS}/${generateRouteString(BusTabs.DRIVER)}`,
          icon: <MoneyCollectOutlined />,
        });
      }

      if (hasPermission("conductor", "conductor:view-paged")) {
        busChildren.push({
          label: BusTabs.CONDUCTOR,
          key: `${ROUTES.BUS}/${generateRouteString(BusTabs.CONDUCTOR)}`,
          icon: <UserOutlined />,
        });
      }

      if (hasPermission("bus-structure", "bus-structure:view-paged")) {
        busChildren.push({
          label: BusTabs.STRUCTURE,
          key: `${ROUTES.BUS}/${generateRouteString(BusTabs.STRUCTURE)}`,
          icon: <BlockOutlined />,
        });
      }

      if (busChildren.length > 0) {
        item.push({
          key: ROUTES.BUS,
          icon: <CarOutlined />,
          label: "Bus",
          children: busChildren,
        });
      }
    }

    const databaseChildren: any[] = [];

    if (
      hasPermission("terms-and-conditions", "terms-and-conditions:view-paged")
    ) {
      databaseChildren.push({
        label: DatabaseTabs.TERMS_CONDITION,
        key: `${ROUTES.DATABASE}/${generateRouteString(
          DatabaseTabs.TERMS_CONDITION
        )}`,
        icon: <PaperClipOutlined />,
      });
    }

    if (
      hasPermission("cancellation-policy", "cancellation-policy:view-paged")
    ) {
      databaseChildren.push({
        label: DatabaseTabs.CANCELLATION_POLICY,
        key: `${ROUTES.DATABASE}/${generateRouteString(
          DatabaseTabs.CANCELLATION_POLICY
        )}`,
        icon: <FileExclamationOutlined />,
      });
    }

    if (hasPermission("amenity", "amenity:view-paged")) {
      databaseChildren.push({
        label: DatabaseTabs.AMENITIES,
        key: `${ROUTES.DATABASE}/${generateRouteString(
          DatabaseTabs.AMENITIES
        )}`,
        icon: <HeartOutlined />,
      });
    }

    if (hasPermission("account", "account:view-paged")) {
      databaseChildren.push({
        label: DatabaseTabs.ACCOUNT,
        key: `${ROUTES.DATABASE}/${generateRouteString(DatabaseTabs.ACCOUNT)}`,
        icon: <AccountBookOutlined />,
      });
    }

    if (hasPermission("city", "city:view-paged")) {
      databaseChildren.push({
        label: DatabaseTabs.CITY,
        key: `${ROUTES.DATABASE}/${generateRouteString(DatabaseTabs.CITY)}`,
        icon: <HomeOutlined />,
      });
    }

    if (databaseChildren.length > 0) {
      item.push({
        key: ROUTES.DATABASE,
        icon: <DatabaseOutlined />,
        label: "Database",
        children: databaseChildren,
      });
    }

    if (hasFeature(["trip", "route"])) {
      const tripChildren: any[] = [];

      if (hasPermission("trip", "trip:view-paged")) {
        tripChildren.push({
          label: TripTabs.REPORT,
          key: `${ROUTES.TRIP}/${generateRouteString(TripTabs.REPORT)}`,
          icon: <FileTextOutlined />,
        });
      }

      if (hasPermission("route", "route:view-paged")) {
        tripChildren.push({
          label: TripTabs.ROUTE,
          key: `${ROUTES.TRIP}/${generateRouteString(TripTabs.ROUTE)}`,
          icon: <CompressOutlined />,
        });
      }

      if (hasPermission("trip", "trip:edit")) {
        tripChildren.push({
          label: TripTabs.UPDATE,
          key: `${ROUTES.TRIP}/${generateRouteString(TripTabs.UPDATE)}`,
          icon: <FormOutlined />,
        });
      }

      if (hasPermission("trip", "trip:create")) {
        tripChildren.push({
          label: TripTabs.GENERATE,
          key: `${ROUTES.TRIP}/${generateRouteString(TripTabs.GENERATE)}`,
          icon: <MergeCellsOutlined />,
        });
      }

      if (hasPermission("trip", "trip:auto-assign")) {
        tripChildren.push({
          label: (
            <>
              Crew Scheduler{" "}
              <span style={{ fontSize: 10, color: "gray" }}>AI-Powered</span>
            </>
          ),
          key: `${ROUTES.TRIP}/${generateRouteString(TripTabs.AUTO_ASSIGN)}`,
          icon: <ClockCircleOutlined />,
        });
      }

      if (hasPermission("trip", "trip:view-allowance")) {
        tripChildren.push({
          label: TripTabs.ALLOWANCE,
          key: `${ROUTES.TRIP}/${generateRouteString(TripTabs.ALLOWANCE)}`,
          icon: <MergeCellsOutlined />,
        });
      }

      if (tripChildren.length > 0) {
        item.push({
          key: ROUTES.TRIP,
          icon: <TwitterOutlined />,
          label: "Trip",
          children: tripChildren,
        });
      }
    }

    if (
      hasFeature("bus-admin") &&
      hasPermission("bus-admin", "bus-admin:view-paged")
    ) {
      const busAdminChildren: ItemType<MenuItemType>[] =
        roles?.payload?.map((r) => ({
          key: `${ROUTES.USER}/${r.id}`,
          icon: <UserOutlined />,
          label: r.name,
        })) || [];

      if (busAdminChildren.length > 0) {
        item.push({
          key: ROUTES.USER,
          icon: <UserOutlined />,
          label: "User",
          children: busAdminChildren,
        });
      }
    }

    if (hasFeature("ticket")) {
      const ticketChildren: any[] = [];

      if (hasPermission("ticket", "ticket:view-paged")) {
        ticketChildren.push({
          label: TicketTabs.TICKETS,
          key: `${ROUTES.TICKET}/${generateRouteString(TicketTabs.TICKETS)}`,
          icon: <PaperClipOutlined />,
        });
      }

      if (hasPermission("refund", "refund:view-paged")) {
        ticketChildren.push({
          label: TicketTabs.CANCELLATION_REQUEST,
          key: `${ROUTES.TICKET}/${generateRouteString(
            TicketTabs.CANCELLATION_REQUEST
          )}`,
          icon: <IssuesCloseOutlined />,
        });
      }

      if (hasPermission("ticket", "ticket:cancel")) {
        ticketChildren.push({
          label: TicketTabs.TICKET_CANCELLATION,
          key: `${ROUTES.TICKET}/${generateRouteString(
            TicketTabs.TICKET_CANCELLATION
          )}`,
          icon: <PaperClipOutlined />,
        });
      }

      if (ticketChildren.length > 0) {
        item.push({
          key: ROUTES.TICKET,
          icon: <CopyOutlined />,
          label: "Ticket",
          children: ticketChildren,
        });
      }
    }

    if (hasFeature("finance-audit")) {
      const auditChildren: any[] = [];

      if (hasPermission("finance-audit", "finance-audit:view-paged")) {
        auditChildren.push({
          label: AuditTabs.CONDUCTOR,
          key: `${ROUTES.AUDIT}/${generateRouteString(AuditTabs.CONDUCTOR)}`,
          icon: <PaperClipOutlined />,
        });

        auditChildren.push({
          label: AuditTabs.AGENT,
          key: `${ROUTES.AUDIT}/${generateRouteString(AuditTabs.AGENT)}`,
          icon: <PaperClipOutlined />,
        });
      }

      if (auditChildren.length > 0) {
        item.push({
          key: ROUTES.AUDIT,
          icon: <MoneyCollectOutlined />,
          label: "Ticket Audit",
          children: auditChildren,
        });
      }
    }

    if (hasFeature("report")) {
      item.push({
        key: ROUTES.REPORT,
        icon: <FileTextOutlined />,
        label: "Report",
        children: [
          {
            label: ReportTabs.OFECHO,
            key: `${ROUTES.REPORT}/${generateRouteString(ReportTabs.OFECHO)}`,
            icon: <PaperClipOutlined />,
          },
          {
            label: ReportTabs.WEEKLY,
            key: `${ROUTES.REPORT}/${generateRouteString(ReportTabs.WEEKLY)}`,
            icon: <PaperClipOutlined />,
          },
          {
            label: ReportTabs.SEMERIT,
            key: `${ROUTES.REPORT}/${generateRouteString(ReportTabs.SEMERIT)}`,
            icon: <PaperClipOutlined />,
          },
          {
            label: ReportTabs.AGENT_SALES_REPORT,
            key: `${ROUTES.REPORT}/${generateRouteString(
              ReportTabs.AGENT_SALES_REPORT
            )}`,
            icon: <PaperClipOutlined />,
          },
          {
            label: ReportTabs.DAILY_TICKETS,
            key: `${ROUTES.REPORT}/${generateRouteString(
              ReportTabs.DAILY_TICKETS
            )}`,
            icon: <PaperClipOutlined />,
          },
          {
            label: ReportTabs.TRIP_SUMMARY_REPORT,
            key: `${ROUTES.REPORT}/${generateRouteString(
              ReportTabs.TRIP_SUMMARY_REPORT
            )}`,
            icon: <PaperClipOutlined />,
          },
          {
            label: ReportTabs.SUB_ROUTE_REPORT,
            key: `${ROUTES.REPORT}/${generateRouteString(
              ReportTabs.SUB_ROUTE_REPORT
            )}`,
            icon: <PaperClipOutlined />,
          },
        ],
      });
    }

    if (hasFeature("erca")) {
      item.push({
        key: ROUTES.ERCA,
        icon: <RedEnvelopeOutlined />,
        label: "ERCA",
        children: [
          {
            label: ERCATabs.SALES_REPORT,
            key: `${ROUTES.ERCA}/${generateRouteString(ERCATabs.SALES_REPORT)}`,
            icon: <PaperClipOutlined />,
          },
          {
            label: ERCATabs.SUMMARY_REPORT,
            key: `${ROUTES.ERCA}/${generateRouteString(
              ERCATabs.SUMMARY_REPORT
            )}`,
            icon: <PaperClipOutlined />,
          },
          {
            label: ERCATabs.Individual,
            key: `${ROUTES.ERCA}/${generateRouteString(ERCATabs.Individual)}`,
            icon: <PaperClipOutlined />,
          },
          {
            label: ERCATabs.TRIP_SALES_REPORT,
            key: `${ROUTES.ERCA}/${generateRouteString(
              ERCATabs.TRIP_SALES_REPORT
            )}`,
            icon: <PaperClipOutlined />,
          },
        ],
      });
    }

    if (hasPermission("erca", "erca:view-all")) {
      item.push({
        key: ROUTES.BUS_RENTAL,
        icon: <CarOutlined />,
        label: "Bus Rental",
      });
    }

    // ERP Integration - accessible to all authenticated users
    item.push({
      key: ROUTES.ERP_INTEGRATION,
      icon: <ApiOutlined />,
      label: "ERP Integration",
    });

    // MOLT Integration - accessible to all authenticated users
    item.push({
      key: ROUTES.MOLT_INTEGRATION,
      icon: <ApiOutlined />,
      label: "MOLT Integration",
    });

    return item;
  };

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      theme="light"
      className="hidden-print"
      width={240}
      style={{ fontWeight: 600 }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingTop: 30,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: collapsed ? "20px" : "24px",
          }}
        >
          <img
            src={Logo}
            width={collapsed ? 50 : 100}
            onClick={() => navigate(ROUTES.ROOT)}
            style={{ cursor: "pointer", marginBottom: "10px" }}
          />

          {!collapsed && (
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: "600",
                  color: "var(--text-secondary)",
                  marginBottom: 4,
                }}
              >
                {busAdminState.payload?.firstName}{" "}
                {busAdminState.payload?.lastName}
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: "#6c757d",
                }}
              >
                {busAdminState.payload?.phoneNo}
              </div>
            </div>
          )}
        </div>
      </div>

      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[tab]}
        openKeys={openKeys}
        onSelect={(e) => onTabSelect(e)}
        onOpenChange={onOpenChange}
        items={getMenuItems()}
        className="sidenav-menu"
        style={{ fontWeight: 600 }}
      />
    </Sider>
  );
};

export default SideNavComponent;
